import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {MatMenuTrigger} from '@angular/material/menu';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {NavigationSelectedMenuItemData} from '@application/helper/navigation-helper/navigation-selected-menu-item-data.interface';
import {OrderLinesGridService} from '@application/helper/orderbook/order-lines-grid.service';
import {PathLayoutTemplate} from '@domain/path-layout-template/path-layout-template';
import {PathLayoutTemplatePath} from '@domain/path-layout-template/path-layout-template-path';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {FreeZone} from '@domain/production-schedule/free-zone/free-zone';
import {ManualNonProductionItemDrawing} from '@domain/production-schedule/manual-non-production-item-drawing';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {RestZone} from '@domain/production-schedule/rest-zone/rest-zone';
import {Permission} from '@domain/profile/permission.enum';
import {PropertyValue} from '@domain/property-value';
import {ColoredYarnSetPosition} from '@domain/textile-data/creel/colored-yarn-set-position';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {HttpPathLayoutTemplatesService} from '@infrastructure/http/path-layout-template/http-path-layout-templates.service';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {ListDrawingJute} from '@presentation/components/drawing-list/list-drawing-jute';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {PathLayoutTemplateService} from '@presentation/components/select-path-layout-template/path-layout-template.service';
import {SelectPathLayoutTemplateComponent} from '@presentation/components/select-path-layout-template/select-path-layout-template.component';
import {AddJuteToPathComponent} from '@presentation/pages/texfab/production-schedule/add/plan/modals/add-jute-to-path/add-jute-to-path.component';
import {WeaveQualityType} from '@presentation/pages/textile-data/machine-quality/overview/weave-quality-type.enum';
import {AnalyticsService} from '@shared/analytics.service';
import {
  AssertionUtils,
  BaseComponent,
  ColorConstants,
  convertCommercialUnit,
  convertDentsToWidthInMeter,
  convertPicksToHeightInMeter,
  convertWidthInMeterToDents,
  DialogBuilderFactoryService,
  DialogComponentData,
  DialogType,
  EnumUtils,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {cloneDeep, round} from 'lodash-es';
import {Subscription, takeUntil} from 'rxjs';
import {RecolorService} from '../../../drawing-library/recolor.service';
import {OrderLinesHelper} from '../../../drawing-library/views/order-lines-helper';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../production-schedule-plan';
import {PathBarComponent} from '../path-item/path-bar.component';
import {MaxLengthInfoTooltipComponent} from './max-length-info-tooltip/max-length-info-tooltip.component';
import {ProductionScheduleBuilderPathLayout} from './production-schedule-builder-path-layout.interface';

@Component({
  selector: 'app-path-layout-component',
  templateUrl: './path-layout.component.html',
  styleUrls: ['./path-layout.component.scss']
})
export class PathLayoutComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public commercialUnit: Unit;
  @Input() public listOfCustomSettings: PropertyValue[];
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public buggyLengthMaxInMM: number;
  @Input() public buggyLengthMinInPicks: number;
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public availableSpaceInBuggyInCommercialUnit: number;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public pathWidths: PathWidth[] = [];
  @Input() public isResizing: boolean;
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public selectedViewMode: ProductionScheduleDrawingViewMode;
  @Output() public canZoomIn: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public canZoomOut: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public productionSchedulePlanChanged: EventEmitter<void> = new EventEmitter<void>();
  @Output() public selectedPathOutput: EventEmitter<ProductionSchedulePath> = new EventEmitter<ProductionSchedulePath>();
  @Output() public selectedRestPathColoredYarnSetOutput: EventEmitter<OverviewListColoredYarnSetWithStartDent> = new EventEmitter<OverviewListColoredYarnSetWithStartDent>();
  @Output() public productionSchedulePathsOfColoredYarnSetOutput: EventEmitter<ProductionSchedulePathsOfColoredYarnSet> = new EventEmitter<ProductionSchedulePathsOfColoredYarnSet>();
  @Output() public hoverPathBarOutput = new EventEmitter<boolean>();
  @ViewChildren('paths') public paths: QueryList<ElementRef>;
  @ViewChildren('pathsForColoredYarnSets') public pathsForColoredYarnSet: QueryList<ElementRef>;
  @ViewChildren('pathBars') public pathBars: QueryList<PathBarComponent>;
  @ViewChild('pathsContainer') public pathsContainer: ElementRef;
  @ViewChild('optionMenuTrigger', {read: MatMenuTrigger}) public optionMenuTrigger: MatMenuTrigger;

  public readonly ADD_MANUAL_JUTE_PERMISSION = Permission.ADD_MANUAL_JUTE;
  public readonly ADD_PATH_LAYOUT_TEMPLATE = Permission.TEXFAB_PATH_LAYOUT_TEMPLATE_EDIT;
  public readonly HEADER_AND_PADDING_OF_PATH = 56;
  public maxWidthForDataElements: number;
  public suggestedRestZones: RestZone[];
  public suggestedFreeZones: FreeZone[];
  public heightOfColoredYarnSetsInPercentage: number[] = [];
  public heightOfPathsInPercentage: ProductionScheduleBuilderPathLayout[] = [];
  public heightOfRestPathsInPercentage: number[] = [];
  public newBuggyLengthMaxInPicks: number;
  public optionMenuTimer: Subscription;
  public minimumBuggyLengthIndicatorPlace: number;
  public maximumBuggyLengthIndicatorPlace: number;
  public isDraggingProductionScheduleItem: number;
  public isActionsMenuOpen: boolean = false;
  public isFillingPath = false;

  private selectedPathLayoutTemplate: PathLayoutTemplate;
  private dialogRef: MatDialogRef<RepositionDialogComponent>;
  private savedScrollTop = 0;

  private readonly DIALOG_WIDTH = 334;
  private readonly REPOSITION_DIALOG_CLASS = 'reposition-dialog';
  private readonly VERTICAL_HOVER_OFFSET = 24;
  private readonly HORIZONTAL_HOVER_OFFSET = -160;

  public constructor(
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly pathLayoutTemplateService: PathLayoutTemplateService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly pathLayoutTemplates: HttpPathLayoutTemplatesService,
    private readonly navigationHelperService: NavigationHelperService<DialogComponentData & NavigationSelectedMenuItemData & NavigationNewItemData>,
    private readonly orderLinesHelper: OrderLinesHelper,
    private readonly orderlinesGridService: OrderLinesGridService,
    private readonly recolorService: RecolorService,
    private readonly zone: NgZone,
    private readonly analyticsService: AnalyticsService
  ) {
    super();
    this.orderLinesHelper.commercialUnit = this.commercialUnit;
  }

  public ngOnInit(): void {
    this.orderlinesGridService
      .getIsFillingPath()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((isFillingPath: boolean) => {
        this.isFillingPath = isFillingPath;
        setTimeout(() => {
          this.isFillingPath = false;
        }, 5000);
      });

    this.recolorService
      .getRecolorErrorCatch()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => this.selectRestPath());
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.setHeightOfPaths();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.productionSchedule?.isFirstChange()) {
      this.setHeightOfPaths();
    }

    this.setProductionScheduleZones();
    this.setMaxWidthForDataElements();

    if (AssertionUtils.isNullOrUndefined(this.selectedPath) && AssertionUtils.isNullOrUndefined(this.selectedRestPathColoredYarnSet)) {
      const path = this.productionSchedule?.productionSchedulePathsOfColoredYarnSets[0]?.productionSchedulePaths[0];
      if (AssertionUtils.isNullOrUndefined(path)) {
        this.selectRestPath();
      } else {
        this.pathSelected(path, this.productionSchedule?.productionSchedulePathsOfColoredYarnSets[0]);
      }
    } else {
      this.updateSelectedPath();
    }

    const emptySelectedMenuItemState = {menuItem: null} as NavigationSelectedMenuItemData;
    const selectedMenuItemState = this.navigationHelperService.getPartialState<NavigationSelectedMenuItemData>(Object.keys(emptySelectedMenuItemState), true);
    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData) => this.setPathLayoutTemplate(newItemId, selectedMenuItemState.menuItem.value),
      () => this.selectPathLayoutTemplate()
    );
  }

  public ngOnDestroy(): void {
    this.dialogRef?.close();
  }

  public canShowSubceedsMinBuggyLengthError(path: ProductionSchedulePath): boolean {
    return this.getTotalHeightsOfProductionSchedulePath(path).totalHeightInPicks < this.buggyLengthMinInPicks;
  }

  public canShowExceedsMaxBuggyLengthError(path: ProductionSchedulePath): boolean {
    return this.getTotalHeightsOfProductionSchedulePath(path).totalHeightInPicks > this.buggyLengthMaxInPicks;
  }

  public autoFillPath(): void {
    this.analyticsService.trackEvent('button clicked', `Auto fill`, 'Auto fill button clicked');
    this.orderlinesGridService.setAutoFillButtonClicked(true);
  }

  public isAutoFillButtonDisabled(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.selectedPath)) {
      let isPathEqualOrLongerThanMaxBuggyLengthOrPathThatIsLonger =
        this.getTotalHeightsOfProductionSchedulePath(this.selectedPath).totalHeightInPicks >=
        this.orderLinesHelper.getMaxBuggyLengthOrPathThatIsLonger(this.productionSchedule, this.buggyLengthMaxInPicks);
      return isPathEqualOrLongerThanMaxBuggyLengthOrPathThatIsLonger || this.isFillingPath || this.selectedViewMode !== ProductionScheduleDrawingViewMode.ORDER_LINES;
    } else {
      return this.isFillingPath || this.selectedViewMode !== ProductionScheduleDrawingViewMode.ORDER_LINES;
    }
  }

  public updateSelectedPath(): void {
    const productionScheduleColoredYarnSet = this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.find((pathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      return pathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.startDent === this.productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.startDent;
    });
    this.selectedPath = productionScheduleColoredYarnSet?.productionSchedulePaths?.find((path: ProductionSchedulePath) => path?.pathNumber === this.selectedPath?.pathNumber);
    this.selectedPathOutput.emit(this.selectedPath);
  }

  public canShowDifference(path: ProductionSchedulePath): boolean {
    let longestPathLengthInPicks = 0;

    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
        longestPathLengthInPicks = Math.max(longestPathLengthInPicks, this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks);
      });
    });

    return this.getTotalHeightsOfProductionSchedulePath(path).totalHeightInPicks < longestPathLengthInPicks;
  }

  public canShowRestPath(productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): boolean {
    let totalPathWidthsInDents = 0;
    productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
      totalPathWidthsInDents += productionSchedulePath?.technicalWidthInDents;
    });

    return totalPathWidthsInDents < productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.technicalWidthInDents;
  }

  public canMovePath(pathIndex: number, productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet, up: boolean): boolean {
    if (up) {
      return pathIndex !== 0;
    } else {
      return productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.length !== pathIndex + 1;
    }
  }

  public isProductionSchedulePhaseVerify(): boolean {
    return this.productionSchedulePhase === ProductionSchedulePhase.VERIFY;
  }

  public deletePath(path: ProductionSchedulePath, productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): void {
    this.productionSchedulePlan.removeProductionSchedulePath(path.pathNumber, productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.startDent);

    if (path.pathNumber === this.selectedPath?.pathNumber) {
      this.selectedPath = undefined;
    }

    if (AssertionUtils.isNullOrUndefined(this.selectedPath) && AssertionUtils.isNullOrUndefined(this.selectedRestPathColoredYarnSet)) {
      this.selectedRestPathColoredYarnSet = this.productionSchedule?.productionSchedulePathsOfColoredYarnSets[0]?.productionScheduleColoredYarnSet;
    }

    this.selectedPathOutput.emit(this.selectedPath);
    this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);
  }

  public movePath(path: ProductionSchedulePath, pathIndex: number, productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet, up: boolean): void {
    this.productionSchedulePlan.moveProductionSchedulePathToColoredYarnSet(
      path,
      productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet,
      productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet,
      up ? pathIndex - 1 : pathIndex + 1,
      pathIndex
    );
  }

  public pathSelected(productionSchedulePath: ProductionSchedulePath, productionSchedulePathsOfColoredYarnSet?: ProductionSchedulePathsOfColoredYarnSet): void {
    if (AssertionUtils.isNullOrUndefined(productionSchedulePath)) {
      this.selectedPath = undefined;
      this.selectedRestPathColoredYarnSet = productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet;
    } else {
      this.selectedRestPathColoredYarnSet = undefined;
      this.selectedPath = productionSchedulePath;
    }
    this.productionSchedulePathsOfColoredYarnSetOutput.emit(productionSchedulePathsOfColoredYarnSet);
    this.selectedPathOutput.emit(this.selectedPath);
    this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);
  }

  public getPathBackgroundColor(productionSchedulePath?: ProductionSchedulePath, productionSchedulePathsOfColoredYarnSet?: ProductionSchedulePathsOfColoredYarnSet): string {
    if (!AssertionUtils.isNullOrUndefined(productionSchedulePath) && this.selectedPath?.pathNumber === productionSchedulePath?.pathNumber) {
      return ColorConstants.TINT_LIGHTBLUE_20;
    } else if (
      !AssertionUtils.isNullOrUndefined(productionSchedulePathsOfColoredYarnSet) &&
      this.selectedRestPathColoredYarnSet?.startDent === productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.startDent
    ) {
      return ColorConstants.TINT_LIGHTBLUE_20;
    } else {
      return ColorConstants.PRIMARY_COLOR_WHITE;
    }
  }

  public isPathChecked(productionSchedulePath?: ProductionSchedulePath, productionSchedulePathsOfColoredYarnSet?: ProductionSchedulePathsOfColoredYarnSet): boolean {
    if (!AssertionUtils.isNullOrUndefined(productionSchedulePath)) {
      return this.selectedPath?.pathNumber === productionSchedulePath?.pathNumber;
    } else if (!AssertionUtils.isNullOrUndefined(productionSchedulePathsOfColoredYarnSet)) {
      return this.selectedRestPathColoredYarnSet?.startDent === productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.startDent;
    }
  }

  public getProductionSchedulePathLabel(productionSchedulePath: ProductionSchedulePath, productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): string {
    if (this.productionSchedule?.machine?.jacquard?.isSisal) {
      const positionTranslation = productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.isBottomPosition() ? 'BOTTOM' : 'TOP';
      return `${this.translate.instant(`TEXTILE_DATA.MACHINE_QUALITY.POST_PROCESSING.CLOTH_TYPE_OPTIONS.${positionTranslation}`, {count: 1})} - ${this.translate.instant('PRODUCTION_ORDER.PATH', {
        count: 1
      })} ${productionSchedulePath?.pathNumber}`;
    } else {
      return `${this.translate.instant('PRODUCTION_ORDER.PATH', {count: 1})} ${productionSchedulePath?.pathNumber}`;
    }
  }

  public openHoverInfoTooltip(event: MouseEvent): void {
    if (event.type === 'mouseup') {
      this.isResizing = false;
      this.hoverPathBarOutput.emit();
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.isResizing) {
      return;
    }

    if (!this.isDraggingProductionScheduleItem) {
      let lengthInBuggyInPicks = 0;
      let lengthInBuggyInCommercialUnit = 0;

      if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy)) {
        lengthInBuggyInPicks = this.productionSchedule.buggy?.amountPlannedInPicks;
        lengthInBuggyInCommercialUnit = Math.round(
          convertCommercialUnit({
            from: {unit: Unit.METER, value: this.productionSchedule.buggy.amountPlannedInMeter},
            to: this.commercialUnit
          })
        );
      } else {
        lengthInBuggyInPicks = this.getLongestPathLengthInPicks();
        lengthInBuggyInCommercialUnit = this.getLongestPathLengthInCommercialUnit();
      }

      this.zone.runOutsideAngular(() => {
        this.dialogRef = this.dialogBuilderFactoryService
          .getBuilder()
          .withWidth(`${this.DIALOG_WIDTH}px`)
          .withClass(this.REPOSITION_DIALOG_CLASS)
          .withCustomBackdrop('no-pointer-events-backdrop')
          .open(RepositionDialogComponent, {
            showArrow: false,
            event,
            sourceElement: event.target,
            component: MaxLengthInfoTooltipComponent,
            positionOfDialog: window.innerWidth - event.clientX > this.DIALOG_WIDTH ? PositionOfDialog.RIGHT : PositionOfDialog.LEFT,
            verticalOffset: this.VERTICAL_HOVER_OFFSET,
            horizontalOffset: this.HORIZONTAL_HOVER_OFFSET,
            moveWithCursor: true,
            buggyLengthMaxInPicks: this.buggyLengthMaxInPicks,
            buggyLengthMaxInCommercialUnit: Math.round(
              convertCommercialUnit({
                from: {unit: Unit.MILLIMETER, value: this.buggyLengthMaxInMM},
                to: this.commercialUnit
              })
            ),
            lengthInBuggyInPicks,
            lengthInBuggyInCommercialUnit,
            commercialUnit: this.commercialUnit
          });
      });
    }
  }

  public selectPathLayoutTemplate(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openSelectGridDialog(SelectPathLayoutTemplateComponent, {
        productionSchedule: this.productionSchedule,
        pathWidths: this.pathWidths,
        reedDensityInDentsPerMeter: this.productionSchedule?.machineQuality?.reedDensity * 1000
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(([pathLayoutTemplate, coloredYarnSetPosition]: [PathLayoutTemplate, ColoredYarnSetPosition]) => {
        this.selectedPathLayoutTemplate = pathLayoutTemplate;
        this.changeDetectorRef.detectChanges();
        if (!AssertionUtils.isNullOrUndefined(this.selectedPathLayoutTemplate)) {
          this.addPathsOfPathLayoutTemplates(coloredYarnSetPosition);
        }
      });
  }

  public getProductionScheduleRestPathLabel(productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet, productionScheduleColoredYarnSetIndex?: number): string {
    if (this.productionSchedule?.machine?.jacquard?.isSisal) {
      const positionTranslation = productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet.isBottomPosition() ? 'BOTTOM' : 'TOP';
      return `${this.translate.instant(`TEXTILE_DATA.MACHINE_QUALITY.POST_PROCESSING.CLOTH_TYPE_OPTIONS.${positionTranslation}`, {count: 1})} - ${this.translate.instant(
        'PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.REST',
        {
          count: 1
        }
      )} ${productionScheduleColoredYarnSetIndex + 1}`;
    } else {
      return `${this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.REST', {count: 1})} ${productionScheduleColoredYarnSetIndex + 1}`;
    }
  }

  public getRemainingLengthInBuggy(): string {
    const lengthInCommercialUnit = convertCommercialUnit({
      from: {unit: Unit.MILLIMETER, value: this.buggyLengthMaxInMM},
      to: this.commercialUnit
    });

    let label: string;

    if (!AssertionUtils.isNullOrUndefined(this.availableSpaceInBuggyInPicks)) {
      label = this.getLabelForMaxLengthOrDifference(this.availableSpaceInBuggyInPicks, this.availableSpaceInBuggyInCommercialUnit);
    } else {
      let longestPathLengthInPicks = this.getLongestPathLengthInPicks();
      let longestPathLengthInCommercialUnit = this.getLongestPathLengthInCommercialUnit();

      const remainingLengthInPicks = this.buggyLengthMaxInPicks - longestPathLengthInPicks;
      const remainingLengthInCommercialUnit = Math.ceil(lengthInCommercialUnit - longestPathLengthInCommercialUnit);

      label = this.getLabelForMaxLengthOrDifference(remainingLengthInPicks, remainingLengthInCommercialUnit);
    }
    return label;
  }

  public getUnitDimensionsForPathLayout(): string {
    let longestPathLengthInPicks = 0;
    let longestPathLengthInMM = 0;

    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
        longestPathLengthInMM = Math.max(longestPathLengthInMM, this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInMM);
        longestPathLengthInPicks = Math.max(longestPathLengthInPicks, this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks);
      });
    });

    if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.buggy) && this.productionSchedule?.buggy?.amountPlannedInPicks > this.buggyLengthMaxInPicks) {
      this.newBuggyLengthMaxInPicks = this.productionSchedule.buggy.amountPlannedInPicks;
    } else if (longestPathLengthInPicks > this.buggyLengthMaxInPicks) {
      this.newBuggyLengthMaxInPicks = longestPathLengthInPicks;
    } else {
      this.newBuggyLengthMaxInPicks = undefined;
    }
    const machineWidthInMeters = convertDentsToWidthInMeter(this.productionSchedule?.machine?.dentsForFabric, this.productionSchedule.machineQuality.reedDensity * 1000);

    const lengthInCommercialUnit = this.convertValueToCommercialUnit(longestPathLengthInMM, Unit.MILLIMETER);
    const widthInCommercialUnit = this.convertValueToCommercialUnit(machineWidthInMeters, Unit.METER);

    return `${this.getLabelForCommercialDimensions(widthInCommercialUnit, lengthInCommercialUnit)} - ${this.getLabelForTechnicalDimensions(
      this.productionSchedule?.machine?.dentsForFabric,
      longestPathLengthInPicks
    )}`;
  }

  public getUnitDimensionsForPath(productionSchedulePath: ProductionSchedulePath): string {
    const totalHeightInMM = this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInMM;
    let widthInCommercialUnit = this.convertValueToCommercialUnit(productionSchedulePath.commercialWidthInMM, Unit.MILLIMETER);
    let lengthInCommercialUnit = Math.round(
      convertCommercialUnit({
        from: {
          value: totalHeightInMM ?? 0,
          unit: Unit.MILLIMETER
        },
        to: this.commercialUnit
      })
    );

    if (lengthInCommercialUnit === 0 || widthInCommercialUnit === 0) {
      lengthInCommercialUnit = productionSchedulePath.totalLengthInPicks
        ? this.convertValueToCommercialUnit(
            convertPicksToHeightInMeter(this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks, this.productionSchedule?.machineQuality?.pickDensity * 1000),
            Unit.METER
          )
        : 0;
      widthInCommercialUnit = productionSchedulePath.technicalWidthInDents
        ? this.convertValueToCommercialUnit(convertDentsToWidthInMeter(productionSchedulePath.technicalWidthInDents, this.productionSchedule?.machineQuality?.reedDensity * 1000), Unit.METER)
        : 0;
    }

    return `${this.getLabelForCommercialDimensions(widthInCommercialUnit, lengthInCommercialUnit)} - ${this.getLabelForTechnicalDimensions(
      productionSchedulePath.technicalWidthInDents,
      this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks ?? 0
    )}`;
  }

  public getUnitDimensionsForPathRest(productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet): string {
    let totalWidthOfPathsOfColoredYarnSetInDents = 0;
    let longestPathLengthInPicks = 0;

    productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
      totalWidthOfPathsOfColoredYarnSetInDents += productionSchedulePath?.technicalWidthInDents;
    });

    productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
      longestPathLengthInPicks = Math.max(longestPathLengthInPicks, this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks);
    });

    let longestPathLengthInMM = 0;

    let remainingWidthInMeter = 0;

    if (isNaN(remainingWidthInMeter) || remainingWidthInMeter === 0) {
      remainingWidthInMeter = convertDentsToWidthInMeter(
        productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.technicalWidthInDents - productionSchedulePathsOfColoredYarnSet.getProductionSchedulePathsWidthInDents(),
        this.productionSchedule?.machineQuality?.reedDensity * 1000
      );
    }

    productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
      if (longestPathLengthInMM < this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInMM) {
        longestPathLengthInMM = this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInMM;
      }
    });

    const lengthInCommercialUnit = this.convertValueToCommercialUnit(longestPathLengthInMM, Unit.MILLIMETER);
    const widthInCommercialUnit = this.convertValueToCommercialUnit(remainingWidthInMeter, Unit.METER);

    return `${this.getLabelForCommercialDimensions(widthInCommercialUnit, lengthInCommercialUnit)} - ${this.getLabelForTechnicalDimensions(
      productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.technicalWidthInDents - totalWidthOfPathsOfColoredYarnSetInDents,
      longestPathLengthInPicks
    )}`;
  }

  public getUnitDimesionsForDifference(productionSchedulePath: ProductionSchedulePath): string {
    let longestPathLengthInPicks = 0;
    let longestPathLengthInMM = 0;

    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionScheduleBaan: ProductionSchedulePath) => {
        longestPathLengthInMM = Math.max(longestPathLengthInMM, this.getTotalHeightsOfProductionSchedulePath(productionScheduleBaan).totalHeightInMM);
        longestPathLengthInPicks = Math.max(longestPathLengthInPicks, this.getTotalHeightsOfProductionSchedulePath(productionScheduleBaan).totalHeightInPicks);
      });
    });

    if (AssertionUtils.isNullOrUndefined(productionSchedulePath.totalLengthInPicks)) {
      productionSchedulePath.totalLengthInPicks = 0;
    }

    if (AssertionUtils.isNullOrUndefined(productionSchedulePath.totalLengthInMM)) {
      productionSchedulePath.totalLengthInMM = 0;
    }

    const differenceFromLongestPathInPicks = longestPathLengthInPicks - this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks;
    const differenceFromLongestPathInCommercialUnit = this.convertValueToCommercialUnit(
      longestPathLengthInMM - this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInMM,
      Unit.MILLIMETER
    );

    return this.getLabelForMaxLengthOrDifference(differenceFromLongestPathInPicks, differenceFromLongestPathInCommercialUnit);
  }

  public getHeightOfPathInColoredYarnSet(coloredYarnSetIndex: number, pathIndex: number): string {
    return `${this.heightOfPathsInPercentage[coloredYarnSetIndex]?.pathHeights[pathIndex]}`;
  }

  public getLabelForPathAndDifferenceDimensions(path: ProductionSchedulePath): string {
    const canShowDifference = this.canShowDifference(path) ? `${this.translate.instant(`PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.DIFFERENCE`)} ${this.getUnitDimesionsForDifference(path)}` : '';
    return `${this.translate.instant(`PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.SIZE`)} ${this.getUnitDimensionsForPath(path)} ${canShowDifference}`;
  }

  public setMaxWidthForDataElements(): void {
    if (!AssertionUtils.isNullOrUndefined(this.pathsContainer) && this.pathsContainer.nativeElement) {
      this.maxWidthForDataElements = this.pathsContainer.nativeElement.offsetWidth - 350;
    }
  }

  public onHoverPathBarOutput(): void {
    this.hoverPathBarOutput.emit();
  }

  public canAddCustomDesignToPath(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.selectedPath);
  }

  public openActionsMenu(): void {
    this.isActionsMenuOpen = true;
  }

  public closeActionsMenu(): void {
    this.isActionsMenuOpen = false;
  }

  public addCustomJuteToPath(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass('add-jute-to-path')
      .withWidth('460px')
      .openDialog(AddJuteToPathComponent, {
        pickDensity: this.productionSchedule.machineQuality.pickDensity,
        picksPerColorLine: this.productionSchedule.machineQuality.weaveStructure.picksPerColorLine,
        weaveStructureId: this.productionSchedule.machineQuality.weaveStructure.id,
        qualityId: this.productionSchedule.machineQuality.id,
        colorSetId: this.productionSchedule.coloredYarnSets.at(0).coloredYarnSet.overviewListColorSet.id,
        juteWidthInDents: this.selectedPath.technicalWidthInDents,
        weaveQualityType: this.productionSchedule.machineQuality.weaveStructure.numberOfGroundYarns > 0 ? WeaveQualityType.VELVET : WeaveQualityType.CARPET
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((juteItem: ManualNonProductionItemDrawing | undefined) => {
        if (juteItem !== undefined) {
          this.addCustomJuteListItemToPath(new ListDrawingJute(juteItem));
        }
      });
  }

  private addCustomJuteListItemToPath(listDrawing: ListDrawing): void {
    const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetFromDent(this.selectedPath.startDent);
    this.productionSchedulePlan.addDrawingToProductionSchedulePath(listDrawing, this.selectedPath.pathNumber, productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet);
  }

  private addPathsOfPathLayoutTemplates(coloredYarnSetPosition: ColoredYarnSetPosition): void {
    const pathTemplateWidthsInDents = this.selectedPathLayoutTemplate?.pathLayoutTemplatePaths?.map((pathLayoutTemplatePath: PathLayoutTemplatePath) => {
      return (
        this.productionSchedulePlan.lookupCommercialWidthInPathWidths(pathLayoutTemplatePath?.widthInMM, this.pathWidths) ??
        convertWidthInMeterToDents(
          convertCommercialUnit({from: {unit: Unit.MILLIMETER, value: pathLayoutTemplatePath.widthInMM}, to: Unit.METER}),
          this.productionSchedule.machineQuality.reedDensity * 1000,
          0
        )
      );
    });

    let startDent = 1;

    if (AssertionUtils.isNullOrUndefined(coloredYarnSetPosition)) {
      this.productionSchedule.productionSchedulePathsOfColoredYarnSets[0].productionSchedulePaths = this.createPathsOfPathTemplates(startDent, pathTemplateWidthsInDents);
    } else {
      if (coloredYarnSetPosition === ColoredYarnSetPosition.BOTTOM) {
        this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet, index: number) => {
          if (productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.isBottomPosition()) {
            this.productionSchedule.productionSchedulePathsOfColoredYarnSets[index].productionSchedulePaths = this.createPathsOfPathTemplates(startDent, pathTemplateWidthsInDents);
          }
        });
      } else if (coloredYarnSetPosition === ColoredYarnSetPosition.TOP) {
        this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet, index: number) => {
          if (!productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.isBottomPosition()) {
            this.productionSchedule.productionSchedulePathsOfColoredYarnSets[index].productionSchedulePaths = this.createPathsOfPathTemplates(startDent, pathTemplateWidthsInDents);
          }
        });
      }
    }

    this.productionSchedulePlan.addPathLayoutTemplates(this.productionSchedule);
    this.selectFirstPath();
  }

  private getTotalHeightsOfProductionSchedulePath(productionSchedulePath: ProductionSchedulePath): {totalHeightInPicks: number; totalHeightInMM: number} {
    let totalHeightInPicks = 0;
    let totalHeightInMM = 0;

    productionSchedulePath?.pathGroups?.forEach((item: ProductionScheduleItemInPathGroup) => {
      totalHeightInPicks = totalHeightInPicks + (item?.technicalDimensions?.heightInPicks + item?.getTechnicalCutbarsLengthInNumberOfPicks()) * item?.quantity;
      totalHeightInMM = totalHeightInMM + (item?.commercialDimensionsInMM?.heightInMM + item?.getCommercialCutbarsLengthInMM()) * item?.quantity;
    });

    return {totalHeightInPicks: totalHeightInPicks ?? 0, totalHeightInMM: totalHeightInMM ?? 0};
  }

  private selectFirstPath(): void {
    this.selectedPath = this.productionSchedule.productionSchedulePathsOfColoredYarnSets[0].productionSchedulePaths[0];
    this.selectedRestPathColoredYarnSet = undefined;
    this.selectedPathOutput.emit(this.selectedPath);
    this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);
  }

  private createPathsOfPathTemplates(startDent: number, pathTemplateWidthsInDents: number[]): ProductionSchedulePath[] {
    return this.selectedPathLayoutTemplate?.pathLayoutTemplatePaths?.map((pathLayoutTemplatePath: PathLayoutTemplatePath, pathIndex: number) => {
      const addedStartDent = startDent;
      startDent += pathTemplateWidthsInDents[pathIndex];
      return new ProductionSchedulePath('', pathIndex + 1, pathLayoutTemplatePath.widthInMM, pathTemplateWidthsInDents[pathIndex], [], addedStartDent, 0, 0);
    });
  }

  private setHeightOfPaths(): void {
    this.heightOfPathsInPercentage = [];
    this.heightOfRestPathsInPercentage = [];
    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((pathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet, cysIndex: number) => {
      this.heightOfColoredYarnSetsInPercentage[cysIndex] = (pathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.technicalWidthInDents / this.productionSchedule?.machine?.dentsForFabric) * 100;
      this.heightOfPathsInPercentage[cysIndex] = {pathHeights: []};

      pathsOfColoredYarnSet?.productionSchedulePaths?.forEach((path: ProductionSchedulePath, pathIndex: number) => {
        this.heightOfPathsInPercentage[cysIndex].pathHeights[pathIndex] = (path.technicalWidthInDents / pathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.technicalWidthInDents) * 100;
      });

      this.heightOfRestPathsInPercentage[cysIndex] = AssertionUtils.isEmpty(this.heightOfPathsInPercentage[cysIndex]?.pathHeights)
        ? 100
        : 100 - this.heightOfPathsInPercentage[cysIndex].pathHeights.reduce((a: number, b: number) => a + b);
    });
  }

  private setProductionScheduleZones(): void {
    if (this.isProductionSchedulePhaseVerify()) {
      if (AssertionUtils.isNullOrUndefined(this.suggestedRestZones)) {
        this.suggestedRestZones = cloneDeep(this.productionSchedule?.restZones);
      }

      if (AssertionUtils.isNullOrUndefined(this.suggestedFreeZones)) {
        this.suggestedFreeZones = cloneDeep(this.productionSchedule?.freeZones);
      }
    } else {
      this.suggestedRestZones = null;
      this.suggestedFreeZones = null;
    }
  }

  private convertValueToCommercialUnit(value: number, unit: Unit): number {
    return round(
      convertCommercialUnit({
        from: {
          value: value ?? 0,
          unit
        },
        to: this.commercialUnit
      }),
      0
    );
  }

  private getLabelForMaxLengthOrDifference(technicalHeightInPicks: number, commercialHeightInCommercialUnit: number): string {
    return `${commercialHeightInCommercialUnit} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, this.commercialUnit)}`)} - ${technicalHeightInPicks} ${this.translate.instant(
      `GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.PICKS)}`
    )}`;
  }

  private getLabelForTechnicalDimensions(technicalWidthInDents: number, technicalHeightInPicks: number): string {
    return `${technicalWidthInDents} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.DENTS)}`)} x ${technicalHeightInPicks} ${this.translate.instant(
      `GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, Unit.PICKS)}`
    )}`;
  }

  private getLabelForCommercialDimensions(commercialWidthInCommercialUnit: number, commercialHeightInCommercialUnit: number): string {
    return `${commercialWidthInCommercialUnit} x ${commercialHeightInCommercialUnit} ${this.translate.instant(`GENERAL.UNIT.${EnumUtils.getKeyFromValue(Unit, this.commercialUnit)}`)}`;
  }

  private selectRestPath(): void {
    this.selectedRestPathColoredYarnSet = this.productionSchedule?.productionSchedulePathsOfColoredYarnSets[0]?.productionScheduleColoredYarnSet;
    this.selectedPath = undefined;
    this.selectedPathOutput.emit(this.selectedPath);
    this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);
    this.productionSchedulePathsOfColoredYarnSetOutput.emit(this.productionSchedule?.productionSchedulePathsOfColoredYarnSets[0]);
  }

  private getLongestPathLengthInPicks(): number {
    let longestPathLengthInPicks = 0;

    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
        longestPathLengthInPicks = Math.max(longestPathLengthInPicks, this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks);
      });
    });

    return longestPathLengthInPicks;
  }

  private getLongestPathLengthInCommercialUnit(): number {
    let longestPathLengthInMM = 0;

    this.productionSchedule?.productionSchedulePathsOfColoredYarnSets?.forEach((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => {
      productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths?.forEach((productionSchedulePath: ProductionSchedulePath) => {
        longestPathLengthInMM = Math.max(longestPathLengthInMM, this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInMM);
      });
    });

    return Math.floor(
      convertCommercialUnit({
        from: {unit: Unit.MILLIMETER, value: longestPathLengthInMM},
        to: this.commercialUnit
      })
    );
  }

  private setPathLayoutTemplate(id: number, coloredYarnSetPosition: number): void {
    this.pathLayoutTemplates
      .getById(id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((pathLayoutTemplate: PathLayoutTemplate) => {
        const ifTemplateHasTooMuchWidth = this.pathLayoutTemplateService.checkIfTemplateHasTooMuchWidth(
          this.productionSchedule,
          pathLayoutTemplate,
          coloredYarnSetPosition,
          this.productionSchedule.machineQuality.reedDensity * 1000,
          this.pathWidths
        );

        const ifTemplateHasOverlappingPaths = this.pathLayoutTemplateService.checkIfTemplateHasOverlappingPaths(
          this.productionSchedule,
          pathLayoutTemplate,
          this.productionSchedule.machineQuality.reedDensity * 1000,
          this.pathWidths
        );

        if (ifTemplateHasTooMuchWidth || ifTemplateHasOverlappingPaths) {
          this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
            titleText: this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.PATH_LAYOUT'),
            messageText: this.translate.instant(ifTemplateHasTooMuchWidth ? 'PATH_LAYOUT_TEMPLATE.TOO_MUCH_WIDTH_ERROR' : 'PATH_LAYOUT_TEMPLATE.OVERLAPPING_COLORSETS_ERROR'),
            type: DialogType.INFORMATION
          });
        } else {
          this.selectedPathLayoutTemplate = pathLayoutTemplate;
          this.changeDetectorRef.detectChanges();
          this.addPathsOfPathLayoutTemplates(coloredYarnSetPosition);
        }
      });
  }
}
